import { api } from '@/lib/api';
import { connectSocket } from '@/lib/socket';
import type { ChatMessage } from '@/types/api';
import { useAuth } from '@/stores/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

interface TypingPerson {
  userId: string;
  fullName: string;
}

/**
 * Estado de un canal de chat. El historial llega por REST y los mensajes
 * nuevos por WebSocket; ambos alimentan la misma lista para que no haya un
 * salto visible entre lo que ya estaba y lo que acaba de llegar.
 */
export function useChat(channelId: string | null) {
  const queryClient = useQueryClient();
  const currentUser = useAuth((state) => state.user);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState<TypingPerson[]>([]);
  const typingTimers = useRef(new Map<string, number>());

  // Historial inicial del canal.
  useEffect(() => {
    if (!channelId) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setTyping([]);

    api
      .get<{ messages: ChatMessage[] }>(`/chat/channels/${channelId}/messages`)
      .then((data) => {
        if (!cancelled) {
          setMessages(data.messages);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMessages([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [channelId]);

  // Eventos en vivo del canal abierto.
  useEffect(() => {
    if (!channelId) {
      return;
    }

    const socket = connectSocket();
    socket.emit('channel:subscribe', { channelId });
    socket.emit('channel:read', { channelId });

    const onNew = (message: ChatMessage) => {
      if (message.channelId !== channelId) {
        // Es de otro canal: solo hay que refrescar el contador de no leidos.
        void queryClient.invalidateQueries({ queryKey: ['channels'] });
        return;
      }
      setMessages((current) => {
        if (current.some((m) => m.id === message.id)) {
          return current;
        }
        const sinProvisional = current.filter(
          (m) => !(m.pending && m.author.id === message.author.id && m.body === message.body),
        );
        return [...sinProvisional, message];
      });
      socket.emit('channel:read', { channelId });
    };

    const onDeleted = ({ messageId, channelId: from }: { messageId: string; channelId: string }) => {
      if (from !== channelId) {
        return;
      }
      setMessages((current) => current.filter((m) => m.id !== messageId));
    };

    const onTypingStart = (payload: { channelId: string; userId: string; fullName: string }) => {
      if (payload.channelId !== channelId) {
        return;
      }
      setTyping((current) =>
        current.some((p) => p.userId === payload.userId)
          ? current
          : [...current, { userId: payload.userId, fullName: payload.fullName }],
      );

      // Red de seguridad: si el "typing:stop" se pierde, el aviso no se queda
      // colgado para siempre.
      const previous = typingTimers.current.get(payload.userId);
      if (previous) {
        window.clearTimeout(previous);
      }
      typingTimers.current.set(
        payload.userId,
        window.setTimeout(() => {
          setTyping((current) => current.filter((p) => p.userId !== payload.userId));
          typingTimers.current.delete(payload.userId);
        }, 4000),
      );
    };

    const onTypingStop = (payload: { channelId: string; userId: string }) => {
      if (payload.channelId !== channelId) {
        return;
      }
      setTyping((current) => current.filter((p) => p.userId !== payload.userId));
    };

    socket.on('message:new', onNew);
    socket.on('message:deleted', onDeleted);
    socket.on('typing:start', onTypingStart);
    socket.on('typing:stop', onTypingStop);

    const timers = typingTimers.current;
    return () => {
      socket.off('message:new', onNew);
      socket.off('message:deleted', onDeleted);
      socket.off('typing:start', onTypingStart);
      socket.off('typing:stop', onTypingStop);
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, [channelId, queryClient]);

  /**
   * Enviar un mensaje.
   *
   * Aparece en el acto marcado como "enviando" y se confirma cuando el servidor
   * acusa recibo. Antes solo se pintaba al volver por `message:new`, mas de un
   * segundo despues y sin ninguna senal, asi que parecia que no se habia
   * mandado y se acababa escribiendolo dos veces.
   */
  const send = useCallback(
    (body: string) => {
      const text = body.trim();
      if (!channelId || !text || !currentUser) {
        return;
      }

      const provisionalId = `pendiente-${Date.now()}`;
      setMessages((current) => [
        ...current,
        {
          id: provisionalId,
          channelId,
          body: text,
          createdAt: new Date().toISOString(),
          editedAt: null,
          author: {
            id: currentUser.id,
            fullName: currentUser.fullName,
            avatarUrl: currentUser.avatarUrl,
            username: currentUser.username,
          },
          replyTo: null,
          pending: true,
        },
      ]);

      const socket = connectSocket();
      socket.emit('typing:stop', { channelId });
      socket.emit('message:send', { channelId, body: text }, (response) => {
        setMessages((current) => {
          // Si la difusion llego primero, el provisional sobra.
          if (current.some((m) => m.id === response.messageId)) {
            return current.filter((m) => m.id !== provisionalId);
          }
          return current.map((m) =>
            m.id === provisionalId ? { ...m, id: response.messageId, pending: false } : m,
          );
        });
        void queryClient.invalidateQueries({ queryKey: ['channels'] });
      });

      // Si el servidor no contesta, se marca para que quede claro que no salio.
      window.setTimeout(() => {
        setMessages((current) =>
          current.map((m) => (m.id === provisionalId ? { ...m, pending: false, failed: true } : m)),
        );
      }, 12_000);
    },
    [channelId, currentUser, queryClient],
  );

  const notifyTyping = useCallback(() => {
    if (channelId) {
      connectSocket().emit('typing:start', { channelId });
    }
  }, [channelId]);

  return { messages, loading, typing, send, notifyTyping };
}
